import React, { useState } from "react";
import logo from "../assets/admin/LogoAndName.svg";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Loading from "../components/layout/Loading";
import { useSession } from "../components/layout/SessionContext";
import CryptoJS from "crypto-js";
import { toast, ToastContainer } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || "default-secret-key";

// Encrypt and save permissions in localStorage
const saveEncryptedPermissions = (permissions) => {
  const encrypted = CryptoJS.AES.encrypt(
    JSON.stringify(permissions),
    SECRET_KEY,
  ).toString();
  localStorage.setItem("permissions", encrypted);
};

const SignIn = () => {
  const navigate = useNavigate();
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "", login: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { setUser, setPermissions } = useSession();

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isPasswordValid = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%?&])[A-Za-z\d@#$!%?&]{8,}$/;
    return regex.test(password);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailAddress(value);

    if (value && !emailPattern.test(value)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (value && !isPasswordValid(value)) {
      setErrors((prev) => ({
        ...prev,
        password:
          "Password must be at least 8 characters, include uppercase, lowercase, number, and special character.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const signIn = async (email, password) => {
    if (errors.email || errors.password || !email || !password) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/login`,
        {
          username: email,
          password,
        },
        {
          withCredentials: true,
        },
      );
      const fullAccess = {
        dashboard: ["view", "edit", "export"],
        order: ["view", "edit", "export"],
        international: ["view", "edit", "export"],
        brand: ["view", "edit", "export"],
        product: ["view", "edit", "export"],
        inventory: ["view", "edit", "export"],
        simSwap: ["view", "edit", "export"],
        customer: ["view", "edit", "export"],
        appBanner: ["view", "edit", "export"],
        referralCode: ["view", "edit", "export"],
        manageUser: ["view", "edit", "export"],
      };

      const moduleRoutes = {
        dashboard: "/dashboard",
        order: "/orders",
        international: "/international",
        brand: "/brands",
        product: "/products",
        inventory: "/inventory",
        simSwap: "/simswap",
        customer: "/customers",
        appBanner: "/appbanner",
        referralCode: "/promocode",
        manageUser: "/manageUsers",
      };

      if (response.status === 200) {
        const { admin, accessControl } = response.data.data;
        localStorage.setItem("token", response.data.data.token);
        localStorage.setItem("username", response.data.data.admin.username);
        localStorage.setItem("adminId", response.data.data.admin.id);
        localStorage.setItem("adminUid", response.data.data.admin.uid);
        // localStorage.setItem(
        //   "admin",
        //   JSON.stringify(response.data.data.admin)
        // );
        const permissions =
          accessControl === "All Access Granted" ? fullAccess : accessControl;
        saveEncryptedPermissions(permissions);

        setUser(admin);
        setPermissions(
          accessControl === "All Access Granted" ? fullAccess : accessControl,
        );

        if (admin.newAdmin) {
          navigate("/create-new-password");
          return;
        }
        if (permissions.dashboard && permissions.dashboard.includes("view")) {
          navigate("/dashboard");
        } else {
          const firstAccessibleModule = Object.keys(permissions).find(
            (module) =>
              permissions[module]?.includes("view") && moduleRoutes[module],
          );

          if (firstAccessibleModule) {
            navigate(moduleRoutes[firstAccessibleModule]); //Correct mapped route
          } else {
            toast.error("You don't have access to any module!");
          }
        }
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          login: "Invalid credentials. Please try again.",
        }));
      }
    } catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        login:
          error.response?.data?.message || "Login failed. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = () => {
    signIn(emailAddress.trim(), password);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && emailAddress.trim() && password.trim()) {
      handleSignIn();
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="flex items-center justify-center min-h-screen mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center space-y-6 w-full max-w-md">
        {/* Logo */}
        <img src={logo} alt="Bereload Logo" className="mx-auto p-2" />
        <div>
          <h2 className="text-text1 text-3xl leading-9 font-extrabold">
            Sign in to your account
          </h2>
        </div>

        {!isLoading ? (
          <div>
            {/* Email Input */}
            <div className="text-left mb-6">
              <input
                type="text"
                placeholder="Email Address"
                className={`base_input w-full p-2 border rounded-md ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                value={emailAddress}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
              />
              {errors.email && (
                <p className="text-[#e23131] text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div className="text-left mb-3">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  className={`base_input text-text1 w-full p-2 pr-10 border rounded-md ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[#e23131] text-sm mt-1">{errors.password}</p>
              )}
            </div>

            <div
              className="self-stretch text-right justify-start text-gray-700 text-base font-medium font-['Inter'] leading-normal cursor-pointer hover:underline"
              onClick={handleForgotPassword}
            >
              Forgotten password
            </div>

            {/* Error Message */}
            {errors.login && (
              <p className="text-[#e23131] text-sm mt-1 text-left">
                {errors.login}
              </p>
            )}

            {/* Sign In Button */}
            <button
              className={`bg-primary text-text1 rounded-md p-4 w-full mt-6 font-medium ${
                !emailAddress || !password
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleSignIn}
              disabled={!emailAddress || !password}
            >
              Sign In
            </button>
          </div>
        ) : (
          <Loading />
        )}
        <ToastContainer
          position="top-right z-40"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
        />
      </div>
    </div>
  );
};

export default SignIn;
